/**
 * Hover-arrow scroller for horizontal product strips. Returns a ref for
 * the scroll container plus `canPrev` / `canNext` flags so callers can
 * render side-edge arrow buttons that page the strip by roughly one
 * viewport on click.
 *
 * ── RTL, AND THE CLAIM THAT USED TO BE HERE ──────────────────────────────
 *
 * This file used to say it was "RTL-safe: relies on scrollBy with negative
 * values for the start direction, which the browser flips automatically."
 * The browser does no such thing. `scrollBy({ left })` is a delta on
 * `scrollLeft`, and in a right-to-left container every current engine runs
 * `scrollLeft` from 0 at the START (hard right) DOWN through negative numbers
 * as the reader moves forward. So a positive delta means "go backward" there,
 * and at rest the strip is already at 0 — its maximum — where a positive
 * delta clamps to nothing at all.
 *
 * The visible effect on a Hebrew shop: the only arrow offered at rest is the
 * forward one, and it is the one that does nothing. Reported as "the arrow
 * doesn't work at all", which is exactly what it was.
 *
 * `direction` is read off the element rather than the document because a
 * strip can sit inside a subtree that overrides it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export default function useStripScroll() {
  /* ── THE REF HAS TO TELL THE HOOK WHEN IT GETS A NODE ────────────────────
   *
   * This was a plain `useRef`, and the subscription effect below reads
   * `ref.current` once on mount and depends on nothing else. Every strip in
   * this app renders `null` until its list arrives — `ShopBrandStrip`,
   * `ShopProductStrip` and `LazyRelated` all open with
   * `if (list.length === 0) return null;` — so on the mount that matters the
   * effect saw `null`, bailed, and never ran again: no `scroll` listener, no
   * `ResizeObserver`, and `canPrev` / `canNext` stuck at `false` for the life
   * of the page.
   *
   * Measured on dk-store at 1440, Hebrew, with arrows wired onto the brand
   * strip: `scrollWidth 3324` inside `clientWidth 1440` — 1884px of brands off
   * screen — and NO arrow rendered, because the row's eleven cards arrive one
   * render after the component mounts. The two strips that already had arrows
   * were hiding the same defect: they are mounted by a parent that has already
   * fetched, so their first render had the node.
   *
   * So the ref is a callback ref that ALSO answers to `.current`, both ways:
   * React drives it by call (`ref={stripRef}`), the rows that keep a second
   * ref on the same element drive it by assignment
   * (`arrowsRef.current = node`), and either one wakes the effects. */
  const nodeRef = useRef(null);
  const [node, setNode] = useState(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const ref = useMemo(() => {
    const attach = (el) => {
      const next = el || null;
      if (nodeRef.current === next) return;
      nodeRef.current = next;
      setNode(next);
    };
    Object.defineProperty(attach, 'current', { get: () => nodeRef.current, set: attach });
    return attach;
  }, []);

  const update = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // Compare against absolute value because RTL scrolls into negative
    // territory in some engines.
    const pos = Math.abs(el.scrollLeft);
    setCanPrev(pos > 4);
    setCanNext(max - pos > 4);
  }, []);

  useEffect(() => {
    const el = node;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [node, update]);

  const scrollByPage = useCallback((direction) => {
    const el = nodeRef.current;
    if (!el) return;
    const amount = Math.max(240, el.clientWidth * 0.85);
    const rtl = typeof window !== 'undefined' && window.getComputedStyle
      ? window.getComputedStyle(el).direction === 'rtl'
      : false;
    // `direction` is the reader's forward/backward; the sign it needs on
    // `scrollLeft` is the opposite one in RTL. See the note at the top.
    el.scrollBy({ left: (rtl ? -1 : 1) * direction * amount, behavior: 'smooth' });
  }, []);

  return { ref, canPrev, canNext, scrollPrev: () => scrollByPage(-1), scrollNext: () => scrollByPage(1), refresh: update };
}
